// Progress store — local-first with opportunistic cloud sync.
//
// Local: packed as JSON in localStorage under PROGRESS_KEY.
// Cloud: mirrored to users/{uid}/progress in Firestore when authenticated.
// Merge policy on boot: per-world max(local, cloud) — no data ever goes
// backward, so network round-trip ordering doesn't cause regressions.
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';

export type WorldProgress = Record<number, number>;

// Per-level best-moves record, keyed by level id (e.g. "w4-l07"). Cloud-synced
// alongside WorldProgress so a player's personal par travels across devices.
// Merge policy on boot: per-level-id min(local, cloud). Lower is always better;
// data never regresses. A hinted clear NEVER writes here — see App.tsx's
// handleSolved. That invariant is load-bearing: if we ever let hints count,
// we need to version-bump the key to invalidate prior tainted records.
export type LevelBests = Record<string, number>;

// Bonus-level clears, keyed by level id (e.g. "bonus-07"). A present key means
// "the player has cleared this bonus at least once" — we don't care about move
// count or replays; this is purely a collectible ledger powering the "found:
// N of 20" footer line. Cloud-synced alongside progress + bests.
// Merge policy: union — once found, always found.
export type BonusFound = Record<string, true>;

// Key versions were bumped in v0.2.1 to reset all local state for playtesting.
// Old keys (.v2 / .v1) become orphaned in localStorage — they're small and
// self-limiting (next bump will obviate them entirely). Name survives the
// reset on purpose: the welcome modal only needs to fire once per device.
const PROGRESS_KEY = 'tessera.progress.v3';
const LEGACY_PROGRESS_KEY = 'tessera.maxLevelIndex.v1';
const BESTS_KEY = 'tessera.bests.v2';
const NAME_KEY = 'tessera.playerName.v1';
const BONUS_KEY = 'tessera.bonusFound.v2';

// Display-name constraints. Deliberately tight: this is a single-player puzzle
// game today, so a name is ceremonial polish, not an identity. We still strip
// control chars and cap length so a pathological paste doesn't break the UI.
const NAME_MAX_LEN = 20;

export function sanitizePlayerName(raw: string): string {
  // Strip control chars, collapse whitespace, trim, truncate.
  const clean = raw
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean.slice(0, NAME_MAX_LEN);
}

export function readLocalName(): string | null {
  try {
    const raw = localStorage.getItem(NAME_KEY);
    if (!raw) return null;
    const clean = sanitizePlayerName(raw);
    return clean.length > 0 ? clean : null;
  } catch {
    return null;
  }
}

export function writeLocalName(name: string | null): void {
  try {
    if (name === null || name === '') localStorage.removeItem(NAME_KEY);
    else localStorage.setItem(NAME_KEY, sanitizePlayerName(name));
  } catch { /* ignore */ }
}

export function readLocal(): WorldProgress {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as WorldProgress;
      if (parsed && typeof parsed === 'object') return parsed;
    }
    // Legacy key fallback is intentionally dropped in v0.2.1 — the playtest
    // reset made it actively harmful (it would rehydrate old progress from
    // pre-v0.1.0 installs). LEGACY_PROGRESS_KEY is retained only so we can
    // proactively prune it from localStorage in case quota matters later.
    void LEGACY_PROGRESS_KEY;
  } catch { /* ignore */ }
  return {};
}

export function writeLocal(p: WorldProgress) {
  try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

export function mergeProgress(a: WorldProgress, b: WorldProgress): WorldProgress {
  const out: WorldProgress = {};
  const keys = new Set<number>([
    ...Object.keys(a).map(Number),
    ...Object.keys(b).map(Number),
  ]);
  for (const k of keys) {
    out[k] = Math.max(a[k] ?? 0, b[k] ?? 0);
  }
  return out;
}

// Combined cloud read: returns both worlds-progress and per-level bests in one
// round trip. Cloud schema is `{ worlds: WorldProgress, bests?: LevelBests,
// updatedAt: number }` — `bests` is optional so older docs (pre-v0.1.15) keep
// working.
export interface CloudState {
  readonly worlds: WorldProgress;
  readonly bests: LevelBests;
  readonly bonus: BonusFound;
}

export async function readCloud(uid: string): Promise<CloudState> {
  try {
    const snap = await getDoc(doc(db, 'users', uid, 'state', 'progress'));
    if (!snap.exists()) return { worlds: {}, bests: {}, bonus: {} };
    const data = snap.data() as { worlds?: WorldProgress; bests?: LevelBests; bonus?: BonusFound };
    return { worlds: data.worlds ?? {}, bests: data.bests ?? {}, bonus: data.bonus ?? {} };
  } catch (e) {
    // Cloud read is best-effort; always fall back to local.
    console.warn('cloud progress read failed', e);
    return { worlds: {}, bests: {}, bonus: {} };
  }
}

export async function writeCloud(
  uid: string,
  worlds: WorldProgress,
  bests: LevelBests,
  bonus: BonusFound,
): Promise<void> {
  try {
    await setDoc(
      doc(db, 'users', uid, 'state', 'progress'),
      { worlds, bests, bonus, updatedAt: Date.now() },
    );
  } catch (e) {
    console.warn('cloud progress write failed', e);
  }
}

// Bests merge: lower is always better, and a missing entry loses to any entry
// present on the other side. Symmetric — order of arguments doesn't matter.
export function mergeBests(a: LevelBests, b: LevelBests): LevelBests {
  const out: LevelBests = {};
  const keys = new Set<string>([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const av = a[k];
    const bv = b[k];
    if (av === undefined) out[k] = bv!;
    else if (bv === undefined) out[k] = av;
    else out[k] = Math.min(av, bv);
  }
  return out;
}

export function readLocalBests(): LevelBests {
  try {
    const raw = localStorage.getItem(BESTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as LevelBests;
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch { /* ignore */ }
  return {};
}

export function writeLocalBests(b: LevelBests) {
  try { localStorage.setItem(BESTS_KEY, JSON.stringify(b)); } catch { /* ignore */ }
}

// Bonus "found" ledger — union merge (no field ever goes from true back to
// missing). Simpler than bests because there's no move count to track.
export function readLocalBonus(): BonusFound {
  try {
    const raw = localStorage.getItem(BONUS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as BonusFound;
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch { /* ignore */ }
  return {};
}

export function writeLocalBonus(b: BonusFound): void {
  try { localStorage.setItem(BONUS_KEY, JSON.stringify(b)); } catch { /* ignore */ }
}

export function mergeBonus(a: BonusFound, b: BonusFound): BonusFound {
  const out: BonusFound = { ...a };
  for (const k of Object.keys(b)) out[k] = true;
  return out;
}
